<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none bg-gray-100" tabindex="0">
        <form @submit.prevent="saveTicket">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="rounded-2xl bg-white border border-gray-200 shadow-sm p-5 md:p-6">
                    <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div class="min-w-0">
                            <h1 class="text-xl font-semibold text-gray-900">{{ $t('Create ticket') }}</h1>
                            <p class="mt-1 text-sm text-gray-500">
                                {{ $t('Create a ticket, assign executors, and configure approval') }}.
                            </p>
                        </div>
                        <div class="flex flex-wrap gap-2">
                            <span class="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">
                                {{ $t('Executors') }}: {{ targetSelectionCount }}
                            </span>
                            <span class="inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                                {{ $t('Approvers') }}: {{ approvalSelectionCount }}
                            </span>
                            <span class="inline-flex items-center px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
                                {{ $t('Attachments') }}: {{ (ticket && ticket.attachments) ? ticket.attachments.length : 0 }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="mt-6 rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden">
                    <loading :status="loading && loading.form !== undefined ? loading.form : true" />

                    <div class="p-4 sm:p-6 space-y-6">
                        <section class="grid grid-cols-1 lg:grid-cols-3 gap-5">
                            <div>
                                <h3 class="text-base font-semibold text-gray-900">{{ $t('Ticket details') }}</h3>
                                <p class="mt-1 text-sm text-gray-500">{{ $t('Basic ticket information') }}.</p>
                            </div>
                            <div class="lg:col-span-2 space-y-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700" for="subject">{{ $t('Subject') }}</label>
                                    <input
                                        id="subject"
                                        v-model.trim="ticket.subject"
                                        :placeholder="$t('Subject')"
                                        class="mt-1 form-input block w-full text-sm"
                                        required
                                    >
                                </div>

                                <div
                                    @paste.capture="handleComposerPaste"
                                    @dragover.prevent
                                    @drop.prevent="handleComposerDrop"
                                >
                                    <label class="block text-sm font-medium text-gray-700" for="ticket_body">{{ $t('Ticket body') }}</label>

                                    <div
                                        v-if="composerImagePreviews.length > 0"
                                        class="ticket-image-preview-strip mt-1 mb-2"
                                    >
                                        <div
                                            v-for="preview in composerImagePreviews"
                                            :key="preview.key"
                                            class="ticket-image-preview-card"
                                        >
                                            <img
                                                :src="preview.url"
                                                :alt="preview.name"
                                                class="ticket-image-preview-img"
                                            >
                                            <button
                                                class="ticket-image-preview-remove"
                                                type="button"
                                                @click="removeComposerImagePreview(preview)"
                                            >
                                                ×
                                            </button>
                                            <div
                                                v-if="preview.status === 'uploading'"
                                                class="ticket-image-preview-status"
                                            >
                                                {{ $t('Uploading') }}
                                            </div>
                                            <div
                                                v-if="preview.status === 'error'"
                                                class="ticket-image-preview-status ticket-image-preview-error"
                                            >
                                                {{ $t('Failed') }}
                                            </div>
                                        </div>
                                    </div>

                                    <textarea
                                        id="ticket_body"
                                        v-model="ticket.body"
                                        :placeholder="$t('Type ticket description')"
                                        class="mt-1 form-textarea block w-full text-sm min-h-40"
                                        rows="7"
                                    ></textarea>
                                    <p class="mt-1 text-xs text-gray-500">
                                        {{ $t('This lightweight editor is used to keep the create page fast') }}.
                                    </p>
                                    <p v-if="maxFileSizeLabel" class="mt-1 text-xs text-gray-400">
                                        {{ $t('Max file size: {size}', { size: maxFileSizeLabel }) }}
                                    </p>
                                </div>

                                <div>
                                    <button
                                        class="btn btn-white rounded-md shadow-sm"
                                        type="button"
                                        @click="selectUploadFile"
                                    >
                                        {{ $t('Upload attachment') }}
                                    </button>
                                    <span v-if="loading.file" class="ml-2 text-xs text-blue-600">
                                        {{ $t('Uploading') }} {{ uploadingFileProgress }}%
                                    </span>
                                </div>

                                <div v-if="nonImageAttachments.length > 0" class="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    <attachment
                                        v-for="item in nonImageAttachments"
                                        :key="item.attachment.id || item.attachment.uuid || item.attachment.name || item.index"
                                        :details="item.attachment"
                                        v-on:remove="removeAttachment(item.index)"
                                    />
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700" for="ticket_started_at">{{ $t('Start date') }}</label>
                                        <input
                                            id="ticket_started_at"
                                            v-model="ticket.started_at"
                                            type="date"
                                            class="mt-1 form-input block w-full text-sm"
                                        >
                                        <p class="mt-1 text-xs text-gray-500">{{ $t('When work on this ticket starts') }}.</p>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700" for="ticket_due_at">{{ $t('End date') }}</label>
                                        <input
                                            id="ticket_due_at"
                                            v-model="ticket.due_at"
                                            :min="ticket.started_at || undefined"
                                            type="date"
                                            class="mt-1 form-input block w-full text-sm"
                                        >
                                        <p class="mt-1 text-xs text-gray-500">{{ $t('Deadline for this ticket to be completed') }}.</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section class="border-t border-gray-200 pt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
                            <div>
                                <h3 class="text-base font-semibold text-gray-900">{{ $t('Ticket executor') }}</h3>
                                <p class="mt-1 text-sm text-gray-500">
                                    {{ $t('Select individual users, departments, or both') }}.
                                </p>
                            </div>
                            <div class="lg:col-span-2 space-y-4">
                                <p class="flex items-start gap-1.5 text-xs text-gray-500">
                                    <svg-vue class="h-3.5 w-3.5 flex-shrink-0 text-blue-400" icon="font-awesome.info-circle-light"></svg-vue>
                                    <span>{{ $t('Your own account is excluded automatically') }}.</span>
                                </p>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div class="rounded-xl border border-gray-200 bg-gray-50 p-3">
                                        <label class="block text-xs font-semibold text-gray-600 mb-2">{{ $t('Per user') }}</label>
                                        <input
                                            v-model.trim="targetUserSearch"
                                            :placeholder="$t('Search user')"
                                            class="form-input block w-full text-sm"
                                            type="text"
                                        >
                                        <div class="mt-2 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                                            <button
                                                v-for="user in filteredTargetUsers"
                                                :key="'target-user-option-' + user.id"
                                                class="w-full flex items-center px-3 py-2 text-left hover:bg-blue-50 focus:outline-none"
                                                type="button"
                                                @click="addTargetUser(user.id)"
                                            >
                                                <img :src="getAvatar(user)" class="h-7 w-7 rounded-full mr-2 object-cover" alt="avatar">
                                                <span class="min-w-0 flex-1">
                                                    <span class="block text-sm text-gray-700 truncate">{{ user.name }}</span>
                                                    <span class="block text-xs text-gray-400 truncate">{{ user.email }}</span>
                                                </span>
                                            </button>
                                            <div v-if="filteredTargetUsers.length === 0" class="px-3 py-2 text-sm text-gray-500">
                                                {{ $t('No users found') }}
                                            </div>
                                        </div>
                                    </div>

                                    <div class="rounded-xl border border-gray-200 bg-gray-50 p-3">
                                        <label class="block text-xs font-semibold text-gray-600 mb-2">{{ $t('Per department') }}</label>
                                        <input
                                            v-model.trim="targetDepartmentSearch"
                                            :placeholder="$t('Search department')"
                                            class="form-input block w-full text-sm"
                                            type="text"
                                        >
                                        <div class="mt-2 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                                            <button
                                                v-for="department in filteredTargetDepartments"
                                                :key="'target-dept-option-' + department.id"
                                                class="w-full flex items-center px-3 py-2 text-left hover:bg-blue-50 focus:outline-none"
                                                type="button"
                                                @click="addTargetDepartment(department.id)"
                                            >
                                                <span class="text-sm text-gray-700 truncate">{{ department.name }}</span>
                                            </button>
                                            <div v-if="filteredTargetDepartments.length === 0" class="px-3 py-2 text-sm text-gray-500">
                                                {{ $t('No departments found') }}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="rounded-xl border border-gray-200 bg-white p-3">
                                    <div class="flex items-center justify-between gap-3 mb-2">
                                        <div class="text-xs font-semibold text-gray-500">{{ $t('Selected executor users') }}</div>
                                        <div v-if="loadingTargetDepartmentUsers" class="text-xs text-blue-600">{{ $t('Loading department users') }}...</div>
                                    </div>
                                    <div v-if="selectedTargetDepartments.length > 0 && targetSelectionCount === 0" class="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-2">
                                        <div class="text-xs font-semibold text-gray-500 mb-2">{{ $t('Selected departments') }}</div>
                                        <div class="flex flex-wrap gap-2">
                                            <span
                                                v-for="department in selectedTargetDepartments"
                                                :key="'target-dept-chip-empty-' + department.id"
                                                class="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs border border-gray-200"
                                            >
                                                {{ department.name }}
                                                <button class="ml-2 text-gray-500 hover:text-red-600" type="button" @click="removeTargetDepartment(department.id)">×</button>
                                            </span>
                                        </div>
                                        <div v-if="loadingTargetDepartmentUsers" class="mt-2 text-xs text-blue-600">
                                            {{ $t('Loading department users') }}...
                                        </div>
                                        <div v-else class="mt-2 text-xs text-red-600">
                                            {{ $t('No eligible executor users found in selected departments') }}.
                                        </div>
                                    </div>
                                    <div v-if="targetSelectionCount === 0" class="text-sm text-red-600">
                                        {{ $t('Select at least one executor') }}.
                                    </div>
                                    <div v-else class="space-y-3">
                                        <div class="flex flex-wrap gap-2">
                                            <span
                                                v-for="user in effectiveTargetUsers"
                                                :key="'target-effective-user-chip-' + user.id"
                                                class="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-blue-800 text-xs border border-blue-100"
                                            >
                                                <img :src="getAvatar(user)" class="h-4 w-4 rounded-full mr-1 object-cover" alt="avatar">
                                                {{ user.name }}
                                                <span v-if="isDepartmentTargetUser(user.id)" class="ml-1 text-blue-500">{{ $t('dept') }}</span>
                                                <button
                                                    class="ml-2 text-blue-600 hover:text-red-600"
                                                    type="button"
                                                    @click="removeEffectiveTargetUser(user.id)"
                                                >×</button>
                                            </span>
                                        </div>
                                        <div v-if="selectedTargetDepartments.length > 0" class="pt-2 border-t border-gray-100">
                                            <div class="text-xs font-semibold text-gray-500 mb-2">{{ $t('Selected departments') }}</div>
                                            <div class="flex flex-wrap gap-2">
                                                <span
                                                    v-for="department in selectedTargetDepartments"
                                                    :key="'target-dept-chip-' + department.id"
                                                    class="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs border border-gray-200"
                                                >
                                                    {{ department.name }}
                                                    <button class="ml-2 text-gray-500 hover:text-red-600" type="button" @click="removeTargetDepartment(department.id)">×</button>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section class="border-t border-gray-200 pt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
                            <div>
                                <h3 class="text-base font-semibold text-gray-900">{{ $t('Approval') }}</h3>
                                <p class="mt-1 text-sm text-gray-500">{{ $t('Configure ticket approval when required') }}.</p>
                            </div>
                            <div class="lg:col-span-2 space-y-4">
                                <input-switch
                                    id="approval_required"
                                    v-model="ticket.approval_required"
                                    :disabled-label="$t('No approval needed')"
                                    :enabled-label="$t('Approval needed')"
                                ></input-switch>

                                <div v-if="ticket.approval_required" class="space-y-4">
                                    <p class="flex items-start gap-1.5 text-xs text-gray-500">
                                        <svg-vue class="h-3.5 w-3.5 flex-shrink-0 text-blue-400" icon="font-awesome.info-circle-light"></svg-vue>
                                        <span>{{ $t('Select individual approvers. Your own account is excluded automatically') }}.</span>
                                    </p>

                                    <div class="rounded-xl border border-gray-200 bg-gray-50 p-3">
                                        <label class="block text-xs font-semibold text-gray-600 mb-2">{{ $t('Per user') }}</label>
                                        <input
                                            v-model.trim="approverSearch"
                                            :placeholder="$t('Search approver')"
                                            class="form-input block w-full text-sm"
                                            type="text"
                                        >
                                        <div class="mt-2 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                                            <button
                                                v-for="user in filteredApprovers"
                                                :key="'approver-user-option-' + user.id"
                                                class="w-full flex items-center px-3 py-2 text-left hover:bg-emerald-50 focus:outline-none"
                                                type="button"
                                                @click="addApproverUser(user.id)"
                                            >
                                                <img :src="getAvatar(user)" class="h-7 w-7 rounded-full mr-2 object-cover" alt="avatar">
                                                <span class="min-w-0 flex-1">
                                                    <span class="block text-sm text-gray-700 truncate">{{ user.name }}</span>
                                                    <span class="block text-xs text-gray-400 truncate">{{ user.email }}</span>
                                                </span>
                                            </button>
                                            <div v-if="filteredApprovers.length === 0" class="px-3 py-2 text-sm text-gray-500">
                                                {{ $t('No users found') }}
                                            </div>
                                        </div>
                                    </div>

                                        <div class="rounded-xl border border-gray-200 bg-white p-3">
                                        <div class="flex items-center justify-between gap-3 mb-2">
                                            <div class="text-xs font-semibold text-gray-500">{{ $t('Selected approver users') }}</div>
                                        </div>
                                        <div v-if="approvalSelectionCount === 0" class="text-sm text-red-600">
                                            {{ $t('Select at least one valid approver') }}.
                                        </div>
                                        <div v-else class="space-y-3">
                                            <div class="flex flex-wrap gap-2">
                                                <span
                                                    v-for="user in effectiveApprovers"
                                                    :key="'approver-effective-user-chip-' + user.id"
                                                    class="inline-flex items-center px-2 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs border border-emerald-100"
                                                >
                                                    <img :src="getAvatar(user)" class="h-4 w-4 rounded-full mr-1 object-cover" alt="avatar">
                                                    {{ user.name }}
                                                    <button
                                                        class="ml-2 text-emerald-600 hover:text-red-600"
                                                        type="button"
                                                        @click="removeApproverUser(user.id)"
                                                    >×</button>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section class="border-t border-gray-200 pt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
                            <div>
                                <h3 class="text-base font-semibold text-gray-900">{{ $t('CC / Diketahui') }}</h3>
                                <p class="mt-1 text-sm text-gray-500">
                                    {{ $t('CC participants can read the ticket and receive notifications, but cannot reply or change it') }}.
                                </p>
                            </div>
                            <div class="lg:col-span-2 space-y-4">
                                <p class="flex items-start gap-1.5 text-xs text-gray-500">
                                    <svg-vue class="h-3.5 w-3.5 flex-shrink-0 text-blue-400" icon="font-awesome.info-circle-light"></svg-vue>
                                    <span>{{ $t('Executors and approvers are hidden from this list — one person has one role per ticket') }}.</span>
                                </p>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div class="rounded-xl border border-gray-200 bg-gray-50 p-3">
                                        <label class="block text-xs font-semibold text-gray-600 mb-2">{{ $t('Per user') }}</label>
                                        <input
                                            v-model.trim="ccUserSearch"
                                            :placeholder="$t('Search user')"
                                            class="form-input block w-full text-sm"
                                            type="text"
                                        >
                                        <div class="mt-2 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                                            <button
                                                v-for="user in filteredCcUsers"
                                                :key="'cc-user-option-' + user.id"
                                                class="w-full flex items-center px-3 py-2 text-left hover:bg-purple-50 focus:outline-none"
                                                type="button"
                                                @click="addCcUser(user.id)"
                                            >
                                                <img :src="getAvatar(user)" class="h-7 w-7 rounded-full mr-2 object-cover" alt="avatar">
                                                <span class="min-w-0 flex-1">
                                                    <span class="block text-sm text-gray-700 truncate">{{ user.name }}</span>
                                                    <span class="block text-xs text-gray-400 truncate">{{ user.email }}</span>
                                                </span>
                                            </button>
                                            <div v-if="filteredCcUsers.length === 0" class="px-3 py-2 text-sm text-gray-500">
                                                {{ $t('No users found') }}
                                            </div>
                                        </div>
                                    </div>

                                    <div class="rounded-xl border border-gray-200 bg-gray-50 p-3">
                                        <label class="block text-xs font-semibold text-gray-600 mb-2">{{ $t('Per department') }}</label>
                                        <input
                                            v-model.trim="ccDepartmentSearch"
                                            :placeholder="$t('Search department')"
                                            class="form-input block w-full text-sm"
                                            type="text"
                                        >
                                        <div class="mt-2 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                                            <button
                                                v-for="department in filteredCcDepartments"
                                                :key="'cc-dept-option-' + department.id"
                                                class="w-full flex items-center px-3 py-2 text-left hover:bg-purple-50 focus:outline-none"
                                                type="button"
                                                @click="addCcDepartment(department.id)"
                                            >
                                                <span class="text-sm text-gray-700 truncate">{{ department.name }}</span>
                                            </button>
                                            <div v-if="filteredCcDepartments.length === 0" class="px-3 py-2 text-sm text-gray-500">
                                                {{ $t('No departments found') }}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="rounded-xl border border-gray-200 bg-white p-3">
                                    <div class="text-xs font-semibold text-gray-500 mb-2">{{ $t('Selected CC') }}</div>
                                    <div v-if="ccSelectionCount === 0" class="text-sm text-gray-400">
                                        {{ $t('No CC selected') }}.
                                    </div>
                                    <div v-else class="flex flex-wrap gap-2">
                                        <span
                                            v-for="user in selectedCcUsers"
                                            :key="'cc-user-chip-' + user.id"
                                            class="inline-flex items-center px-2 py-1 rounded-full bg-purple-50 text-purple-800 text-xs border border-purple-100"
                                        >
                                            <img :src="getAvatar(user)" class="h-4 w-4 rounded-full mr-1 object-cover" alt="avatar">
                                            {{ user.name }}
                                            <button
                                                class="ml-2 text-purple-600 hover:text-red-600"
                                                type="button"
                                                @click="removeCcUser(user.id)"
                                            >×</button>
                                        </span>
                                        <span
                                            v-for="department in selectedCcDepartments"
                                            :key="'cc-dept-chip-' + department.id"
                                            class="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs border border-gray-200"
                                        >
                                            {{ department.name }}
                                            <button
                                                class="ml-2 text-gray-500 hover:text-red-600"
                                                type="button"
                                                @click="removeCcDepartment(department.id)"
                                            >×</button>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>

                    <div class="bg-gray-50 border-t border-gray-200 px-4 py-3 sm:px-6">
                        <div class="flex flex-col sm:flex-row sm:justify-end sm:items-center gap-3">
                            <span v-if="targetSelectionCount < 1" class="text-xs text-red-600 sm:mr-2">
                                {{ $t('Select at least one executor before submitting') }}.
                            </span>
                            <span v-else-if="ticket.approval_required && approvalSelectionCount < 1" class="text-xs text-red-600 sm:mr-2">
                                {{ $t('Select at least one valid approver before submitting') }}.
                            </span>
                            <span v-else class="text-xs text-gray-500 sm:mr-2">
                                {{ $t('Review executors and approval before submitting') }}.
                            </span>
                            <router-link class="btn btn-secondary shadow-sm rounded-md sm:mr-2 w-full sm:w-auto text-center" to="/dashboard/tickets">
                                {{ $t('Cancel') }}
                            </router-link>
                            <button
                                :class="!canSubmitTicket || submitting ? 'opacity-50 cursor-not-allowed' : ''"
                                :disabled="!canSubmitTicket || submitting || (loading && loading.form) || (loading && loading.file)"
                                class="btn btn-green shadow-sm rounded-md w-full sm:w-auto"
                                type="submit"
                            >
                                {{ submitting ? $t('Saving...') : $t('Create ticket') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
        <input ref="fileInput" hidden type="file" @change="uploadFile($event)">
    </main>
</template>

<script>
export default {
    name: "new",
    metaInfo() {
        return {
            title: this.$i18n.t('Create ticket')
        };
    },
    data() {
        return {
            loading: {
                form: true,
                file: false,
            },
            submitting: false,
            uploadingFileProgress: 0,
            ticket: {
                subject: null,
                target_type: 'users',
                target_user_ids: [],
                target_department_ids: [],
                excluded_target_user_ids: [],
                status_id: 1,
                // Default start date & target date = hari ini (tanggal lokal).
                started_at: new Date().getFullYear() + '-'
                    + String(new Date().getMonth() + 1).padStart(2, '0') + '-'
                    + String(new Date().getDate()).padStart(2, '0'),
                due_at: new Date().getFullYear() + '-'
                    + String(new Date().getMonth() + 1).padStart(2, '0') + '-'
                    + String(new Date().getDate()).padStart(2, '0'),
                approval_required: false,
                approval_type: 'users',
                approval_user_ids: [],
                approval_department_ids: [],
                cc_user_ids: [],
                cc_department_ids: [],
                body: '',
                attachments: [],
            },
            targetUserList: [],
            targetDepartmentList: [],
            approverList: [],
            targetDepartmentUsers: [],
            loadingTargetDepartmentUsers: false,
            targetUserSearch: '',
            targetDepartmentSearch: '',
            approverSearch: '',
            ccUserSearch: '',
            ccDepartmentSearch: '',
            composerImagePreviews: [],
            pendingUploadCount: 0,
        };
    },
    computed: {
        authUserId() {
            return this.$store && this.$store.state.user ? Number(this.$store.state.user.id) : null;
        },
        normalizedTargetUserIds() {
            return this.normalizeIdArray(this.ticket.target_user_ids).filter((id) => id !== this.authUserId);
        },
        normalizedTargetDepartmentIds() {
            return this.normalizeIdArray(this.ticket.target_department_ids);
        },
        normalizedApprovalUserIds() {
            return this.normalizeIdArray(this.ticket.approval_user_ids).filter((id) => id !== this.authUserId);
        },
        normalizedCcUserIds() {
            return this.normalizeIdArray(this.ticket.cc_user_ids).filter((id) => id !== this.authUserId);
        },
        normalizedCcDepartmentIds() {
            return this.normalizeIdArray(this.ticket.cc_department_ids);
        },
        selectedTargetUsers() {
            return this.usersByIds(this.targetUserList, this.normalizedTargetUserIds);
        },
        selectedApprovers() {
            return this.usersByIds(this.approverList, this.normalizedApprovalUserIds);
        },
        selectedCcUsers() {
            return this.usersByIds(this.targetUserList, this.normalizedCcUserIds);
        },
        selectedCcDepartments() {
            return this.itemsByIds(this.targetDepartmentList, this.normalizedCcDepartmentIds);
        },
        ccSelectionCount() {
            return this.selectedCcUsers.length + this.selectedCcDepartments.length;
        },
        selectedTargetDepartments() {
            return this.itemsByIds(this.targetDepartmentList, this.normalizedTargetDepartmentIds);
        },
        targetDepartmentUserIds() {
            return this.targetDepartmentUsers
                .map((user) => Number(user.id))
                .filter((id) => !!id);
        },
        normalizedExcludedTargetUserIds() {
            return this.normalizeIdArray(this.ticket.excluded_target_user_ids);
        },
        activeDepartmentUsers() {
            const excludedIds = this.normalizedExcludedTargetUserIds;
            return this.targetDepartmentUsers.filter((user) => excludedIds.indexOf(Number(user.id)) === -1);
        },
        activeDepartmentUserIds() {
            return this.activeDepartmentUsers
                .map((user) => Number(user.id))
                .filter((id) => !!id);
        },
        effectiveTargetUsers() {
            return this.uniqueUsers(this.selectedTargetUsers.concat(this.activeDepartmentUsers));
        },
        effectiveApprovers() {
            return this.selectedApprovers;
        },
        targetSelectionCount() {
            return this.effectiveTargetUsers.length;
        },
        approvalSelectionCount() {
            if (!this.ticket.approval_required) {
                return 0;
            }
            return this.effectiveApprovers.length;
        },
        canSubmitTicket() {
            if (this.targetSelectionCount < 1) {
                return false;
            }
            if (this.ticket.approval_required && this.approvalSelectionCount < 1) {
                return false;
            }
            return true;
        },
        filteredTargetUsers() {
            // User yang sudah dipilih sebagai CC tidak boleh muncul lagi di
            // daftar eksekutor — satu orang satu peran per tiket.
            return this.filterUsers(
                this.targetUserList,
                this.targetUserSearch,
                this.mergeIds(
                    this.normalizedTargetUserIds,
                    this.activeDepartmentUserIds,
                    this.normalizedCcUserIds
                )
            );
        },
        filteredApprovers() {
            return this.filterUsers(
                this.approverList,
                this.approverSearch,
                this.mergeIds(
                    this.normalizedApprovalUserIds,
                    this.normalizedCcUserIds
                )
            );
        },
        filteredCcUsers() {
            // Daftar opsi CC menyembunyikan secara reaktif: yang sudah jadi CC,
            // eksekutor, dan approver — satu orang satu peran per tiket.
            return this.filterUsers(
                this.targetUserList,
                this.ccUserSearch,
                this.mergeIds(
                    this.normalizedCcUserIds,
                    this.normalizedTargetUserIds,
                    this.normalizedApprovalUserIds
                )
            );
        },
        filteredCcDepartments() {
            return this.filterItems(this.targetDepartmentList, this.ccDepartmentSearch, this.normalizedCcDepartmentIds);
        },
        filteredTargetDepartments() {
            return this.filterItems(this.targetDepartmentList, this.targetDepartmentSearch, this.normalizedTargetDepartmentIds);
        },
        nonImageAttachments() {
            return (this.ticket.attachments || [])
                .map((attachment, index) => ({ attachment, index }))
                .filter((item) => !this.isImageAttachment(item.attachment));
        },
        maxFileSize() {
            return Number(
                (typeof window !== 'undefined' && window.app && window.app.upload_max_size) || 0
            );
        },
        maxFileSizeLabel() {
            const bytes = this.maxFileSize;
            if (!bytes) {
                return '';
            }
            const mb = bytes / (1024 * 1024);
            if (mb >= 1024) {
                const gb = mb / 1024;
                return (Number.isInteger(gb) ? gb : gb.toFixed(1)) + ' GB';
            }
            return (Number.isInteger(mb) ? mb : mb.toFixed(1)) + ' MB';
        },
    },
    watch: {
        'ticket.approval_required'(value) {
            if (!value) {
                this.ticket.approval_user_ids = [];
            }
        },
    },
    mounted() {
        this.getFilters();
        document.addEventListener('paste', this.handleDocumentPaste, true);
        window.addEventListener('paste', this.handleDocumentPaste, true);
    },
    beforeDestroy() {
        document.removeEventListener('paste', this.handleDocumentPaste, true);
        window.removeEventListener('paste', this.handleDocumentPaste, true);
        this.revokeComposerImagePreviews();
    },
    methods: {
        getFilters() {
            this.loading.form = true;
            axios.get('api/dashboard/tickets/filters', {
                params: {
                    context: 'assignment',
                    _t: Date.now(),
                }
            }).then((response) => {
                this.targetUserList = this.excludeInvalidUsers(response.data.targets || response.data.agents || []);
                this.targetDepartmentList = response.data.target_departments || response.data.departments || [];
                this.approverList = this.excludeInvalidUsers(response.data.approvers || response.data.agents || []);

                // Status default Open saat create ticket (status_id: 1 sudah di data)
                this.loading.form = false;
                this.sanitizeSelections();
                this.fetchTargetDepartmentUsers();
            }).catch(() => {
                this.loading.form = false;
            });
        },
        saveTicket() {
            if (this.submitting || (this.loading && this.loading.form) || (this.loading && this.loading.file)) {
                return;
            }

            this.sanitizeSelections();

            if (!this.canSubmitTicket) {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.targetSelectionCount < 1
                        ? this.$i18n.t('Select at least one executor').toString()
                        : this.$i18n.t('Select at least one valid approver').toString(),
                    type: 'error'
                });
                return;
            }

            this.submitting = true;
            if (this.loading) {
                this.loading.form = true;
            }

            axios.post('api/dashboard/tickets', this.buildTicketPayload()).then((response) => {
                this.$notify({
                    title: this.$i18n.t('Success').toString(),
                    text: this.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                this.$router.push('/dashboard/tickets/' + response.data.ticket.uuid + '/manage');
            }).catch(() => {
                this.loading.form = false;
            }).finally(() => {
                this.submitting = false;
            });
        },
        buildTicketPayload() {
            return Object.assign({}, this.ticket, {
                target_type: 'mixed',
                approval_type: 'mixed',
                target_user_ids: this.normalizedTargetUserIds,
                target_department_ids: this.normalizedTargetDepartmentIds,
                excluded_target_user_ids: this.normalizedExcludedTargetUserIds,
                approval_user_ids: this.ticket.approval_required ? this.normalizedApprovalUserIds : [],
                approval_department_ids: [],
                cc_type: 'mixed',
                cc_user_ids: this.normalizedCcUserIds,
                cc_department_ids: this.normalizedCcDepartmentIds,
            });
        },
        sanitizeSelections() {
            this.ticket.target_user_ids = this.normalizedTargetUserIds.filter((id) => {
                return this.activeDepartmentUserIds.indexOf(Number(id)) === -1;
            });
            this.ticket.target_department_ids = this.normalizedTargetDepartmentIds;
        },
        fetchTargetDepartmentUsers() {
            const departmentIds = this.normalizedTargetDepartmentIds;

            if (departmentIds.length < 1) {
                this.targetDepartmentUsers = [];
                return;
            }

            this.loadingTargetDepartmentUsers = true;

            axios.get('api/dashboard/tickets/filters', {
                params: {
                    context: 'department-users',
                    purpose: 'target',
                    departments: departmentIds,
                    _t: Date.now(),
                }
            }).then((response) => {
                this.targetDepartmentUsers = this.excludeInvalidUsers(response.data.users || []);
                this.removeTargetUsersCoveredByDepartments();
            }).finally(() => {
                this.loadingTargetDepartmentUsers = false;
            });
        },
        selectUploadFile() {
            if (!this.loading.file) {
                this.$refs.fileInput.click();
                return;
            }
            this.$notify({
                title: this.$i18n.t('Error').toString(),
                text: this.$i18n.t('A file is being uploaded').toString(),
                type: 'warning'
            });
        },
        uploadFile(e) {
            const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
            if (!file) {
                return;
            }
            this.uploadFiles([file], 'file');
            this.$refs.fileInput.value = null;
        },
        handleDocumentPaste(event) {
            // Listener dokumen/window dipasang supaya paste gambar tetap
            // terpakai walau fokus tidak berada tepat di textarea body.
            this.handleComposerPaste(event);
        },
        handleComposerPaste(event) {
            const sourceEvent = event && event.originalEvent ? event.originalEvent : event;
            const clipboardData = sourceEvent && sourceEvent.clipboardData
                ? sourceEvent.clipboardData
                : null;

            if (!clipboardData) {
                return;
            }

            const files = this.extractImageFilesFromClipboard(clipboardData);

            if (files.length < 1) {
                return;
            }

            // Satu event paste bisa lewat beberapa listener (window, dokumen,
            // dan elemen body), jadi event-nya ditandai agar hanya diproses sekali.
            if (sourceEvent.__ticketImagePasteHandled) {
                if (typeof sourceEvent.preventDefault === 'function') {
                    sourceEvent.preventDefault();
                }
                return;
            }

            sourceEvent.__ticketImagePasteHandled = true;

            if (typeof sourceEvent.preventDefault === 'function') {
                sourceEvent.preventDefault();
            }
            if (typeof sourceEvent.stopPropagation === 'function') {
                sourceEvent.stopPropagation();
            }

            this.uploadFiles(files, 'paste');
        },
        extractImageFilesFromClipboard(clipboardData) {
            const files = [];

            if (clipboardData.items) {
                Array.prototype.slice.call(clipboardData.items).forEach((item) => {
                    if (item.kind === 'file') {
                        const file = item.getAsFile();
                        if (file && this.isImageFile(file)) {
                            files.push(file);
                        }
                    }
                });
            }

            if (files.length < 1 && clipboardData.files) {
                Array.prototype.slice.call(clipboardData.files).forEach((file) => {
                    if (this.isImageFile(file)) {
                        files.push(file);
                    }
                });
            }

            return files;
        },
        handleComposerDrop(event) {
            const files = event && event.dataTransfer && event.dataTransfer.files
                ? Array.prototype.slice.call(event.dataTransfer.files)
                : [];

            if (files.length < 1) {
                return;
            }

            this.uploadFiles(files, 'drop');
        },
        uploadFiles(files, source = 'file') {
            const list = Array.prototype.slice.call(files || []);

            if (list.length < 1) {
                return;
            }

            list.forEach((file) => {
                if (this.maxFileSize && file.size > this.maxFileSize) {
                    this.$notify({
                        title: this.$i18n.t('Error').toString(),
                        text: this.$i18n
                            .t('The file exceeds the maximum size of {size}', {
                                size: this.maxFileSizeLabel
                            })
                            .toString(),
                        type: 'warning'
                    });
                    return;
                }

                if (this.isImageFile(file)) {
                    this.uploadSingleImage(file, source);
                } else {
                    this.uploadSingleAttachment(file);
                }
            });
        },
        uploadSingleAttachment(file) {
            const formData = new FormData();

            formData.append('file', file);
            this.pendingUploadCount += 1;
            this.loading.file = true;

            axios.post('api/dashboard/tickets/attachments', formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            }).then((response) => {
                this.ticket.attachments.push(response.data);
            }).catch(() => {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('File upload failed').toString(),
                    type: 'error'
                });
            }).finally(() => {
                this.pendingUploadCount = Math.max(this.pendingUploadCount - 1, 0);
                this.loading.file = this.pendingUploadCount > 0;
            });
        },
        uploadSingleImage(file, source = 'file') {
            const preview = this.createComposerImagePreview(file, source);
            const formData = new FormData();

            formData.append('file', file);
            this.pendingUploadCount += 1;
            this.loading.file = true;

            axios.post('api/dashboard/tickets/attachments', formData, {
                headers: {'Content-Type': 'multipart/form-data'},
                onUploadProgress: (progressEvent) => {
                    if (!progressEvent.total) {
                        return;
                    }
                    const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    preview.progress = percent;
                }
            }).then((response) => {
                preview.status = 'uploaded';
                preview.progress = 100;
                preview.attachment = response.data;
                preview.attachmentId = this.getAttachmentId(response.data);
                this.ticket.attachments.push(response.data);
            }).catch(() => {
                preview.status = 'error';
                preview.progress = 0;
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('Image upload failed').toString(),
                    type: 'error'
                });
            }).finally(() => {
                this.pendingUploadCount = Math.max(this.pendingUploadCount - 1, 0);
                this.loading.file = this.pendingUploadCount > 0;
                if (!this.loading.file) {
                    this.uploadingFileProgress = 0;
                }
            });
        },
        createComposerImagePreview(file, source = 'file') {
            const preview = {
                key: Date.now() + '-' + Math.random().toString(16).slice(2),
                name: file.name || (source === 'paste' ? 'pasted-image.png' : 'image.png'),
                size: file.size || 0,
                type: file.type || 'image/png',
                status: 'uploading',
                progress: 0,
                url: URL.createObjectURL(file),
                attachment: null,
                attachmentId: null
            };

            this.composerImagePreviews.push(preview);

            return preview;
        },
        removeComposerImagePreview(preview) {
            if (!preview) {
                return;
            }

            if (preview.url) {
                URL.revokeObjectURL(preview.url);
            }

            this.composerImagePreviews = this.composerImagePreviews.filter((item) => item.key !== preview.key);

            if (preview.attachmentId) {
                this.ticket.attachments = this.ticket.attachments.filter((attachment) => {
                    return this.getAttachmentId(attachment) !== preview.attachmentId;
                });
            }
        },
        revokeComposerImagePreviews() {
            (this.composerImagePreviews || []).forEach((preview) => {
                if (preview && preview.url) {
                    URL.revokeObjectURL(preview.url);
                }
            });
        },
        isImageFile(file) {
            return !!(file && file.type && file.type.indexOf('image/') === 0);
        },
        isImageAttachment(attachment) {
            if (!attachment) {
                return false;
            }

            const mimeType = attachment.mime_type || attachment.mime || attachment.type || attachment.file_type || '';
            const name = this.getAttachmentName(attachment).toLowerCase();

            return (
                String(mimeType).indexOf('image/') === 0 ||
                /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)
            );
        },
        getAttachmentName(attachment) {
            if (!attachment) {
                return 'image';
            }

            return (
                attachment.name ||
                attachment.file_name ||
                attachment.filename ||
                attachment.original_name ||
                attachment.original_filename ||
                'image'
            );
        },
        getAttachmentId(attachment) {
            if (!attachment) {
                return null;
            }

            return attachment.id || attachment.uuid || attachment.attachment_id || attachment.name || null;
        },
        removeAttachment(index) {
            this.ticket.attachments.splice(index, 1);
        },
        addTargetUser(userId) {
            const id = Number(userId);
            if (!id) {
                this.targetUserSearch = '';
                return;
            }

            // User ter-cover departemen terpilih: bila sedang di-exclude
            // (sempat di-unselect dari chip), pilih lagi = keluar dari
            // exclude supaya aktif kembali lewat departemen. Tanpa ini
            // klik pada opsinya diabaikan diam-diam.
            if (this.targetDepartmentUserIds.indexOf(id) > -1) {
                this.ticket.excluded_target_user_ids = this.removeId(this.ticket.excluded_target_user_ids, id);
                this.targetUserSearch = '';
                return;
            }

            // User yang sama boleh menjadi executor sekaligus approver pada ticket yang sama.
            this.ticket.target_user_ids = this.addUniqueId(this.ticket.target_user_ids, id);
            this.targetUserSearch = '';
        },
        removeTargetUser(userId) {
            this.ticket.target_user_ids = this.removeId(this.ticket.target_user_ids, userId);
        },
        removeEffectiveTargetUser(userId) {
            const id = Number(userId);
            if (this.isDirectTargetUser(id)) {
                this.removeTargetUser(id);
                return;
            }
            // User berasal dari departemen: masukkan ke daftar exclude agar
            // tidak ikut terpilih otomatis, tapi tetap bisa dipilih manual.
            this.ticket.excluded_target_user_ids = this.addUniqueId(this.ticket.excluded_target_user_ids, id);
            this.pruneEmptiedTargetDepartments();
        },
        pruneEmptiedTargetDepartments() {
            const excludedIds = this.normalizedExcludedTargetUserIds;
            const removed = [];
            this.normalizedTargetDepartmentIds.forEach((departmentId) => {
                const hasActiveUser = this.targetDepartmentUsers.some((user) => {
                    const deptIds = user.department_ids || [];
                    return deptIds.indexOf(Number(departmentId)) > -1
                        && excludedIds.indexOf(Number(user.id)) === -1;
                });
                if (!hasActiveUser) {
                    removed.push(Number(departmentId));
                }
            });
            if (removed.length < 1) {
                return;
            }
            // Departemen yang sudah tidak punya satu user pun di-unselect otomatis.
            this.ticket.target_department_ids = this.normalizedTargetDepartmentIds.filter((id) => {
                return removed.indexOf(Number(id)) === -1;
            });
            // Exclude user yang tidak lagi tercover departemen terpilih lain tidak perlu dipertahankan.
            const remainingDeptIds = this.normalizedTargetDepartmentIds;
            this.ticket.excluded_target_user_ids = this.normalizedExcludedTargetUserIds.filter((id) => {
                const user = this.targetDepartmentUsers.find((item) => Number(item.id) === Number(id));
                if (!user) {
                    return false;
                }
                const deptIds = user.department_ids || [];
                return remainingDeptIds.some((deptId) => deptIds.indexOf(Number(deptId)) > -1);
            });
            this.fetchTargetDepartmentUsers();
        },
        addTargetDepartment(departmentId) {
            this.ticket.target_department_ids = this.addUniqueId(this.ticket.target_department_ids, departmentId);
            this.targetDepartmentSearch = '';
            this.fetchTargetDepartmentUsers();
        },
        removeTargetDepartment(departmentId) {
            this.ticket.target_department_ids = this.removeId(this.ticket.target_department_ids, departmentId);
            this.fetchTargetDepartmentUsers();
        },
        addApproverUser(userId) {
            const id = Number(userId);
            if (!id) {
                this.approverSearch = '';
                return;
            }
            this.ticket.approval_user_ids = this.addUniqueId(this.ticket.approval_user_ids, id);
            this.approverSearch = '';
        },
        removeApproverUser(userId) {
            this.ticket.approval_user_ids = this.removeId(this.ticket.approval_user_ids, userId);
        },
        addCcUser(userId) {
            const id = Number(userId);
            if (!id) {
                this.ccUserSearch = '';
                return;
            }
            this.ticket.cc_user_ids = this.addUniqueId(this.ticket.cc_user_ids, id);
            this.ccUserSearch = '';
        },
        removeCcUser(userId) {
            this.ticket.cc_user_ids = this.removeId(this.ticket.cc_user_ids, userId);
        },
        addCcDepartment(departmentId) {
            this.ticket.cc_department_ids = this.addUniqueId(this.ticket.cc_department_ids, departmentId);
            this.ccDepartmentSearch = '';
        },
        removeCcDepartment(departmentId) {
            this.ticket.cc_department_ids = this.removeId(this.ticket.cc_department_ids, departmentId);
        },
        removeTargetUsersCoveredByDepartments() {
            const departmentUserIds = this.activeDepartmentUserIds;
            if (departmentUserIds.length < 1) {
                return;
            }
            this.ticket.target_user_ids = this.normalizedTargetUserIds.filter((id) => {
                return departmentUserIds.indexOf(Number(id)) === -1;
            });
        },
        mergeIds() {
            const seen = {};
            const result = [];
            Array.prototype.slice.call(arguments).forEach((values) => {
                (values || []).forEach((value) => {
                    const id = Number(value);
                    if (!id || seen[id]) {
                        return;
                    }
                    seen[id] = true;
                    result.push(id);
                });
            });
            return result;
        },
        addUniqueId(values, id) {
            const normalized = this.normalizeIdArray(values);
            id = Number(id);
            if (!id || normalized.indexOf(id) > -1) {
                return normalized;
            }
            normalized.push(id);
            return normalized;
        },
        removeId(values, id) {
            id = Number(id);
            return this.normalizeIdArray(values).filter((value) => value !== id);
        },
        normalizeIdArray(values) {
            if (!Array.isArray(values)) {
                values = values === null || values === undefined || values === '' ? [] : [values];
            }
            const seen = {};
            const result = [];
            values.forEach((value) => {
                const id = this.toId(value);
                if (!id || seen[id]) {
                    return;
                }
                seen[id] = true;
                result.push(id);
            });
            return result;
        },
        toId(value) {
            if (value && typeof value === 'object') {
                value = value.id;
            }
            const id = Number(value);
            return Number.isNaN(id) ? null : id;
        },
        excludeSelf(users) {
            return this.excludeInvalidUsers(users);
        },
        excludeInvalidUsers(users) {
            return (users || []).filter((user) => {
                return Number(user.id) !== this.authUserId && !this.isAdminUser(user);
            });
        },
        isAdminUser(user) {
            return !!(user && Number(user.role_id) === 1);
        },
        uniqueUsers(users) {
            const seen = {};
            return this.excludeInvalidUsers(users || []).filter((user) => {
                const id = Number(user.id);
                if (!id || seen[id]) {
                    return false;
                }
                seen[id] = true;
                return true;
            });
        },
        isDirectTargetUser(userId) {
            return this.normalizedTargetUserIds.indexOf(Number(userId)) > -1;
        },
        isDepartmentTargetUser(userId) {
            return this.targetDepartmentUsers.some((user) => Number(user.id) === Number(userId));
        },
        isDirectApproverUser(userId) {
            return this.normalizedApprovalUserIds.indexOf(Number(userId)) > -1;
        },
        usersByIds(users, ids) {
            return (users || []).filter((user) => ids.indexOf(Number(user.id)) > -1);
        },
        itemsByIds(items, ids) {
            return (items || []).filter((item) => ids.indexOf(Number(item.id)) > -1);
        },
        filterUsers(users, query, selectedIds) {
            return this.filterItems(users, query, selectedIds);
        },
        filterItems(items, query, selectedIds) {
            const text = String(query || '').toLowerCase().trim();
            return (items || [])
                .filter((item) => selectedIds.indexOf(Number(item.id)) === -1)
                .filter((item) => {
                    if (!text) {
                        return true;
                    }
                    return String(item.name || '').toLowerCase().indexOf(text) > -1
                        || String(item.email || '').toLowerCase().indexOf(text) > -1;
                })
                .slice(0, 30);
        },
        getAvatar(user) {
            if (!user) {
                return '';
            }
            return user.avatar && user.avatar !== 'gravatar' ? user.avatar : user.gravatar;
        },
    },
};
</script>

<style scoped>
.ticket-image-preview-strip {
    display: flex;
    gap: 0.65rem;
    overflow-x: auto;
    padding-bottom: 0.25rem;
}

.ticket-image-preview-card {
    width: 5.25rem;
    height: 5.25rem;
    border-radius: 0.85rem;
    border: 1px solid #bfdbfe;
    overflow: hidden;
    position: relative;
    flex: 0 0 auto;
    background: #eff6ff;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.ticket-image-preview-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.ticket-image-preview-remove {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    width: 1.35rem;
    height: 1.35rem;
    border-radius: 9999px;
    background: rgba(15, 23, 42, 0.72);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    line-height: 1;
}

.ticket-image-preview-status {
    position: absolute;
    inset: auto 0 0 0;
    padding: 0.25rem;
    background: rgba(37, 99, 235, 0.88);
    color: #ffffff;
    font-size: 0.65rem;
    font-weight: 700;
    text-align: center;
}

.ticket-image-preview-error {
    background: rgba(220, 38, 38, 0.9);
}
</style>
